import { useState, useEffect, useMemo, useContext } from "react";
import { useTranslation } from "react-i18next";
import { getTicket } from "@services/tickets";
import { Ticket } from "@services/tickets/types";
import DrawerContent from "./DrawerContent";
import { TicketListContext } from "../context";
import { useErrorNotification } from "@common/components/Notification";
import { FormContextProvider } from "./context/formContext";
import { CloseCaseTicketProvider } from "./CloseCaseDialog/context";
import TicketExpandingDrawer from "./TicketExpandingDrawer";

type TicketDrawerProps = {
  onClose?: (saved?: boolean) => void;
};
/**
 * A ticket drawer that doesn't need the TicketListContext
 * Used to be able to create tickets from wherever in the app.
 */
export const SeededTicketDrawer = ({
  onClose = () => {},
  ...props
}: TicketDrawerProps & { ticket?: Partial<Ticket> }) => {
  const handleClose = (saved: boolean = false) => {
    onClose(saved);
  };
  const content = useMemo(
    () => <DrawerContent onSave={() => handleClose(true)} />,
    [props.ticket, onClose]
  );

  return (
    <FormContextProvider
      ticket={props.ticket}
      reset={async () => {}}
      openDrawer={props.ticket !== undefined}
    >
      <TicketExpandingDrawer
        open={props.ticket !== undefined}
        onClose={() => handleClose(false)}
      >
        <CloseCaseTicketProvider>{content}</CloseCaseTicketProvider>
      </TicketExpandingDrawer>
    </FormContextProvider>
  );
};

const TicketDrawer = (props: TicketDrawerProps) => {
  const { t } = useTranslation();
  const { onClose = () => {} } = props;
  const [ticket, setTicket] = useState<Ticket>();
  const { ticketId, isNewTicket, dispatch, list } =
    useContext(TicketListContext);
  const dispatchError = useErrorNotification();

  useEffect(() => {
    reset();
  }, [ticketId]);

  const reset = async () => {
    if (ticketId !== undefined) {
      try {
        setTicket(await getTicket(ticketId));
      } catch (error) {
        dispatchError(error, { resourceName: t("Ticket") });
      }
    } else setTicket(undefined);
  };

  const handleClose = (saved?: boolean) => {
    setTicket(undefined);
    dispatch("closeTicket");
    onClose(saved);
  };

  const content = useMemo(
    () => <DrawerContent onSave={(b: boolean) => handleClose(b)} />,
    [ticket, onClose]
  );

  const hasNewEvents =
    ticketId !== undefined &&
    list?.find(({ id }) => id === ticketId)?.events !==
      ticket?.details?.events?.length;

  return (
    <FormContextProvider
      ticket={ticket}
      reset={reset}
      openDrawer={isNewTicket || Boolean(ticketId)}
    >
      <TicketExpandingDrawer
        open={(ticketId !== undefined && ticket !== undefined) || isNewTicket}
        onClose={() => handleClose(hasNewEvents)}
      >
        <CloseCaseTicketProvider>{content}</CloseCaseTicketProvider>
      </TicketExpandingDrawer>
    </FormContextProvider>
  );
};

export default TicketDrawer;
